/* eslint-disable camelcase */
import { DeactivatedSettingsKey } from "../store/ducks/companies.ducks"
import { getDineroAmount } from "./money"
import Dinero from "dinero.js"
import { v4 as uuidv4 } from "uuid"

import { CurrencyCode } from "./currencyUtils"
import {
  AllAccounts,
  AllCompanyAccounts,
  AutoliquidatedCustomVatRate,
  AutoliquidationVat,
  MainCurrencyCode,
} from "../store/ducks/inputMask.ducks"
import {
  IsOnboardingFinishedType,
  VatAccountPayload,
  VatType,
  vatTypeArray,
} from "../store/ducks/companySettings.ducks"
import { BuyOrSell, buyOrSell } from "./company"
import { MerchantCode } from "../store/ducks/merchantCodes.ducks"
import { Account, AccountBillOfExchange } from "../store/ducks/accounts.ducks"
import { FullDocumentForInputMaskWithNewMerchant } from "../components/inputMask/FullDocumentsList"
import { toString } from "ramda"
import {
  DocumentSource,
  GoodsOrServices,
} from "../store/ducks/fullDocuments.ducks"

export type EntryDatumType =
  | "deposit"
  | "reverse_deposit"
  | "tax_excluded"
  | "tax_included"
  | "construction_vat"
  | "eu_reverse_vat"
  | "eu_vat"
  | "eu_goods_reverse_vat"
  | "eu_goods_vat"
  | "world_reverse_vat"
  | "world_vat"
  | "world_goods_reverse_vat"
  | "world_goods_vat"
  | "construction_reverse_vat"
  | "vat"
  | "custom"

export const magicCheckBoxDatumTypes = ["vat", "tax_excluded"] as const
export type MagicCheckBoxDatumTypes = (typeof magicCheckBoxDatumTypes)[number]

export const transferGoodsDatumTypes = [
  "goods_transfer_vat",
  "tobacco_goods_transfer",
  "tobacco_goods_transfer_commission",
  "monetics_goods_transfer",
  "monetics_goods_transfer_commission",
  "scratch_games_goods_transfer",
  "scratch_games_goods_transfer_commission",
  "scratch_games_paid_lots_goods_transfer",
  "draw_games_goods_transfer",
  "draw_games_paid_lots_goods_transfer",
  "draw_games_goods_transfer_commission",
  "tax_stamps_goods_transfer",
  "tax_stamps_goods_transfer_commission",
  "postage_stamps_goods_transfer",
  "postage_stamps_goods_transfer_commission",
  "metro_goods_transfer",
  "metro_goods_transfer_commission",
  "press_goods_transfer",
  "press_goods_transfer_commission",
  "other_goods_transfer",
  "other_goods_transfer_commission",
  "public_receivables_goods_transfer",
  "public_receivables_goods_transfer_commission",
] as const

export type TransferGoodsDatumType = (typeof transferGoodsDatumTypes)[number]

export function isTransferGoodsDatumType(
  datum_type: AllEntryDatumTypes
): datum_type is TransferGoodsDatumType {
  return transferGoodsDatumTypes.includes(datum_type as TransferGoodsDatumType)
}

export const billOfExchangeDatumTypes = [
  "tax_excluded_bill_of_exchange",
  "tax_included_bill_of_exchange",
] as const

export type BillOfExchangeDatumTypes = (typeof billOfExchangeDatumTypes)[number]

export type AllEntryDatumTypes =
  | EntryDatumType
  | TransferGoodsDatumType
  | BillOfExchangeDatumTypes

export type VatRateValue = 0 | 2.1 | 5.5 | 8.5 | 10 | 13 | 20

const LINES_TO_ADD_CUSTOM = ["custom_debit", "custom_credit"]
const LINES_TO_ADD_WITHOUT_VAT_AND_CUSTOM = [
  "deposit",
  "reverse_deposit",
  "eu_vat",
  "eu_goods_vat",
  "world_vat",
  "world_goods_vat",
  "construction_vat",
  "tax_excluded_bill_of_exchange",
] as const
const LINES_TO_ADD_WITHOUT_VAT = [
  ...LINES_TO_ADD_CUSTOM,
  ...LINES_TO_ADD_WITHOUT_VAT_AND_CUSTOM,
] as const

const LINES_TO_EXCLUDE_IF_SYSCOHADA: (typeof LINES_TO_ADD_WITHOUT_VAT)[number][] =
  ["construction_vat"] as const

export type CreditOrDebit = "credit" | "debit"

type LineToAddTuple = typeof LINES_TO_ADD_WITHOUT_VAT
export type LineToAdd = LineToAddTuple[number] | string

type SourceType =
  | "human"
  | "ocr_mark_1"
  | "ocr_triplet"
  | "ocr_amount"
  | "input_mask"

export const autoliquidatedVatTypes = [
  "eu_vat",
  "eu_goods_vat",
  "world_vat",
  "world_goods_vat",
  "construction_vat",
]

export interface WritingLine {
  amount: string
  datum_type: AllEntryDatumTypes
  writing_line_uuid: string
  direction: CreditOrDebit
  account: WritingLineAccount | null
  source: SourceType
  currency_code: CurrencyCode
  vat_rate: VatRateValue
}

export interface WritingLinePayload {
  amount: string
  merchant_code_id: number | null
  datum_type: AllEntryDatumTypes
  writing_line_uuid: string
  direction: CreditOrDebit
  account: WritingLineAccount | null
  source: SourceType
  currency_code: CurrencyCode
  currency_amount: number | null
  exchange_rate: number | null
  exchange_rate_date: string | null
  vat_rate: string
  is_fuel_vat_line: boolean
  partial_vat_recovery_percentage: number | null
  unit: Unit
  unit_quantity: number | null
  unit_description: string | null
  unit_2: Unit
  unit_2_quantity: number | null
  unit_2_description: string | null
  analytic_id: number | null
  new_analytic_value: string | null
}

export interface UnitValues {
  unit: Unit
  unit_quantity: number | null
  unit_description: string | null
  unit_2: Unit
  unit_2_quantity: number | null
  unit_2_description: string | null
}

interface Unit {
  id: number | null
}

interface WritingLineAccount {
  id: number
  number: string
  details: string
}

export type WritingLines = WritingLine[]

export interface Document {
  id: number
  batch_document_id: number
  created_at: Date
  updated_at: Date
  file_name: string
  page_number: number
  full_document_id: number
  page_number_in_full_document: number
  preview_file_name: string
}

export type DocumentType = "invoice" | "credit_note" | "bill_of_exchange"

export interface FullDocumentForInputMask {
  documents: Document[]
  fd_id: number
  document_type: DocumentType
  buy_or_sell: "buy" | "sell"
  fd_status: string
  display_url: string
  journal_code: string
  journal_id: number | null
  periodicity_date_start: string | null
  periodicity_date_end: string | null
  fd_document_date: string
  fd_document_due_date: string | null
  fd_document_reference: string | null
  fd_automatic_reference: string | null
  legal_entity_id: number
  fd_user_file_name: string
  original_file_name: string
  merchant_code: string
  merchant_code_to_create: string
  merchant_code_id: number
  merchant_name: string
  merchant_id: number
  writing_lines: WritingLinePayload[]
  writing_lines_aggregated: boolean
  merchant_buy_default_account_id: number | null
  merchant_buy_vat_default_account_id: number | null
  merchant_sell_default_account_id: number | null
  merchant_sell_vat_default_account_id: number | null
  goods_or_services: GoodsOrServices | null
  document_source: DocumentSource | null
  previously_deactivated_because: DeactivatedSettingsKey | null
  free_text_full_doc: string | null
  ocr_ttc: number | null
  payment_method: string | null
  full_document_file_name: string | null
  pages_count: number
}

export interface DeactivatedDocumentInWritingForInputMask {
  documents: Document[]
  batch_document_id: number
  buy_or_sell: null
  deactivated_because: DeactivatedSettingsKey
  document_type: null
  fd_created_at: Date
  fd_id: number
  fd_status: "deactivated"
  original_batch_name: string
  user_batch_name: null | string
  is_being_reactivated?: boolean
  display_url: string
  ocr_ttc: number | null
  full_document_file_name: string | null
  pages_count: number
}

function getLinesToAddExceptVat({
  isSyscohada,
  autoliquidated_custom_vat_rates,
}: {
  isSyscohada: boolean
  autoliquidated_custom_vat_rates: AutoliquidatedCustomVatRate[]
}) {
  return [
    ...LINES_TO_ADD_WITHOUT_VAT.filter(
      (type) => !(isSyscohada && LINES_TO_EXCLUDE_IF_SYSCOHADA.includes(type))
    ),
    ...autoliquidated_custom_vat_rates.map(
      (customRate) =>
        `${customRate.autoliquidated_type}_custom_${customRate.rate.replace(
          ".0",
          ""
        )}`
    ),
  ].sort()
}

export function getLinesOptions({
  documentType,
  allowedVatRates,
  goods_or_services,
  document_source,
  buyOrSell,
  isSyscohada,
  autoliquidated_custom_vat_rates,
}: {
  documentType: DocumentType
  allowedVatRates: number[]
  goods_or_services: GoodsOrServices
  document_source: DocumentSource
  buyOrSell: BuyOrSell
  isSyscohada: boolean
  autoliquidated_custom_vat_rates: AutoliquidatedCustomVatRate[]
}): string[] {
  if (documentType === "bill_of_exchange")
    return ["tax_excluded_bill_of_exchange"] as const

  if (goods_or_services === "on_site_consumption") return ["0"] as const

  const allLinesTypes = [
    ...LINES_TO_ADD_CUSTOM,
    "0",
    ...allowedVatRates.map(toString),
    ...getLinesToAddExceptVat({
      isSyscohada,
      autoliquidated_custom_vat_rates,
    }),
  ]

  const filterByDocumentSource = allLinesTypes.reduce<LineToAdd[]>(
    (acc, line) => {
      if (
        document_source === "fr" &&
        (line.startsWith("eu_") || line.startsWith("world_"))
      )
        return acc
      else if (
        document_source === "eu" &&
        !(
          line.startsWith("eu_") ||
          line.includes("deposit") ||
          line.includes("custom")
        )
      )
        return acc
      else if (
        document_source === "world" &&
        !(
          line.startsWith("world_") ||
          line.includes("deposit") ||
          (line.includes("custom") && !line.includes("eu_"))
        )
      )
        return acc
      else acc.push(line)
      return acc
    },
    []
  )

  const filterByGoodsOrService = filterByDocumentSource.reduce<LineToAdd[]>(
    (acc, line) => {
      if (
        goods_or_services === "goods" &&
        ["eu_vat", "world_vat"].includes(line)
      )
        return acc
      if (goods_or_services === "goods" && line.includes("eu_custom"))
        return acc
      if (goods_or_services === "goods" && line.includes("world_custom"))
        return acc
      else if (goods_or_services === "services" && line.includes("goods"))
        return acc
      else acc.push(line)
      return acc
    },
    []
  )

  const removedBillOfExchange = filterByGoodsOrService.filter(
    (l) => l !== "tax_excluded_bill_of_exchange"
  )

  const shouldAddZero =
    buyOrSell === "sell" && !removedBillOfExchange.includes("0")
  const filteredOptions = shouldAddZero
    ? [...removedBillOfExchange, "0"]
    : removedBillOfExchange

  return filteredOptions
}

export function isFullDocumentDeactivated(
  fullDocument:
    | DeactivatedDocumentInWritingForInputMask
    | FullDocumentForInputMask
): fullDocument is DeactivatedDocumentInWritingForInputMask {
  return (
    (fullDocument as DeactivatedDocumentInWritingForInputMask)
      .deactivated_because !== undefined
  )
}

export const generateEmptyTaxIncluded = ({
  currency,
}: {
  currency: MainCurrencyCode
}): WritingLinePayload[] => {
  return [
    {
      amount: "0.00",
      datum_type: "tax_included",
      writing_line_uuid: uuidv4(),
      direction: "credit",
      account: null,
      source: "input_mask",
      currency_code: currency,
      currency_amount: 0,
      vat_rate: "0",
      exchange_rate: null,
      exchange_rate_date: null,
      merchant_code_id: null,
      is_fuel_vat_line: false,
      partial_vat_recovery_percentage: null,
      unit: { id: null },
      unit_quantity: null,
      unit_description: null,
      unit_2: { id: null },
      unit_2_quantity: null,
      unit_2_description: null,
      analytic_id: null,
      new_analytic_value: null,
    },
  ]
}

export const updateAmount = ({
  newAmount,
  newCurrencyAmount,
  writingLine,
  existingLines,
  currency,
}: {
  newAmount: string
  newCurrencyAmount: string
  writingLine: WritingLinePayload
  existingLines: WritingLinePayload[]
  currency: MainCurrencyCode
}) => {
  const modifiedLines = [...existingLines].map((l) => ({
    ...l,
    currency_code: currency as CurrencyCode,
  }))
  const lineToModifyIndex = modifiedLines.findIndex(
    (l) =>
      l.writing_line_uuid === writingLine.writing_line_uuid &&
      l.datum_type === writingLine.datum_type
  )
  if (lineToModifyIndex === -1) {
    return modifiedLines
  }

  modifiedLines[lineToModifyIndex].amount = newAmount.replace(",", ".")
  modifiedLines[lineToModifyIndex].currency_amount =
    writingLine.source === "input_mask"
      ? Number(newCurrencyAmount.replace(",", "."))
      : writingLine.currency_amount

  if (writingLine.datum_type === "tax_excluded") {
    const newAmountForVat = ["", ","].includes(newAmount) ? "0" : newAmount
    const taxIncludedAmount = getDineroAmount(newAmountForVat)

    const associatedVatLines = modifiedLines.filter(
      (l) =>
        l.writing_line_uuid === writingLine.writing_line_uuid &&
        [
          "vat",
          "construction_vat",
          "eu_reverse_vat",
          "eu_vat",
          "eu_goods_reverse_vat",
          "eu_goods_vat",
          "world_reverse_vat",
          "world_vat",
          "world_goods_reverse_vat",
          "world_goods_vat",
          "construction_reverse_vat",
        ].includes(l.datum_type)
    )

    associatedVatLines.forEach((vatLine) => {
      const lineIndex = modifiedLines.findIndex(
        (l) =>
          l.writing_line_uuid === writingLine.writing_line_uuid &&
          l.datum_type === vatLine.datum_type
      )

      let vatRate = 0
      if (modifiedLines[lineIndex].vat_rate !== null) {
        vatRate = Number(modifiedLines[lineIndex].vat_rate) / 100
      } else {
        vatRate = Number("0.2")
      }

      const newVatAmount = taxIncludedAmount.multiply(vatRate).toUnit()
      modifiedLines[lineIndex].amount = newVatAmount.toString()
    })
  }

  const updatedLines = updateTaxIncluded(modifiedLines)

  return updatedLines
}

export const updateVat = (
  newVatRate: number,
  writingLine: WritingLinePayload,
  existingLines: WritingLinePayload[],
  buy_or_sell: buyOrSell,
  goods_or_services: GoodsOrServices,
  vat_accounts: VatAccountsWithRate[],
  all_accounts: AllAccounts | null
) => {
  const modifiedLines = [...existingLines].map((l) => ({
    ...l,
  }))
  const lineToModifyIndex = modifiedLines.findIndex(
    (l) =>
      l.writing_line_uuid === writingLine.writing_line_uuid &&
      l.datum_type === writingLine.datum_type
  )
  if (lineToModifyIndex === -1) {
    return modifiedLines
  }

  if (writingLine.datum_type !== "vat") {
    return modifiedLines
  }

  let taxExcludedLine = modifiedLines.find(
    (l) =>
      l.datum_type === "tax_excluded" &&
      l.writing_line_uuid === writingLine.writing_line_uuid
  )

  const amount = taxExcludedLine?.amount
  const newAmountForVat = ["", ","].includes(amount || "0")
    ? "0"
    : amount || "0"
  const taxIncludedAmount = getDineroAmount(newAmountForVat)

  const lineIndex = modifiedLines.findIndex(
    (l) =>
      l.writing_line_uuid === writingLine.writing_line_uuid &&
      l.datum_type === "vat"
  )

  let vatRate = 0
  if (modifiedLines[lineIndex].vat_rate !== null) {
    vatRate = Number(newVatRate) / 100
    modifiedLines[lineIndex].vat_rate = String(newVatRate)
    const new_vat_account = getAccountForGoodsAndService({
      buy_or_sell: buy_or_sell,
      goods_or_services: goods_or_services,
      vat_accounts: vat_accounts,
      vat_rate: String(newVatRate),
      all_accounts: all_accounts,
    })
    modifiedLines[lineIndex].account =
      new_vat_account || modifiedLines[lineIndex].account
    let taxEcludedIndex = modifiedLines.findIndex(
      (l) =>
        l.datum_type === "tax_excluded" &&
        l.writing_line_uuid === writingLine.writing_line_uuid
    )
    modifiedLines[taxEcludedIndex].vat_rate = String(newVatRate)
  } else {
    vatRate = Number("0.2")
  }

  const newVatAmount = taxIncludedAmount.multiply(vatRate).toUnit()
  modifiedLines[lineIndex].amount = newVatAmount.toString()

  const updatedLines = updateTaxIncluded(modifiedLines)
  return updatedLines
}

export const increaseCents = ({
  writingLine,
  existingLines,
}: {
  writingLine: WritingLinePayload
  existingLines: WritingLinePayload[]
}) => {
  const existingAmount = getDineroAmount(writingLine.amount)
  const oneCent = Dinero({ amount: 1 })
  const newAmount = existingAmount
    .add(oneCent)
    .toUnit()
    .toString()
    .replace(",", ".")

  const modifiedLines = [...existingLines]
  const lineToModifyIndex = modifiedLines.findIndex(
    (l) =>
      l.writing_line_uuid === writingLine.writing_line_uuid &&
      l.datum_type === writingLine.datum_type
  )
  if (lineToModifyIndex === -1) {
    return modifiedLines
  }

  modifiedLines[lineToModifyIndex].amount = newAmount.replace(",", ".")

  if (vatTypeArray.includes(writingLine.datum_type as VatType)) {
    const oppositeVatType = writingLine.datum_type.includes("_reverse_vat")
      ? writingLine.datum_type.replace("_reverse_vat", "_vat")
      : writingLine.datum_type.replace("_vat", "_reverse_vat")

    const lineToModifyIndex = modifiedLines.findIndex(
      (l) =>
        l.writing_line_uuid === writingLine.writing_line_uuid &&
        l.datum_type === oppositeVatType
    )
    modifiedLines[lineToModifyIndex].amount = newAmount.replace(",", ".")
  }

  const updatedLines = updateTaxIncluded(modifiedLines)

  return updatedLines
}

export const decreaseCents = ({
  writingLine,
  existingLines,
}: {
  writingLine: WritingLinePayload
  existingLines: WritingLinePayload[]
}) => {
  const existingAmount = getDineroAmount(writingLine.amount)
  const oneCent = Dinero({ amount: 1 })
  const newAmount = existingAmount
    .subtract(oneCent)
    .toUnit()
    .toString()
    .replace(",", ".")

  const modifiedLines = [...existingLines]
  const lineToModifyIndex = modifiedLines.findIndex(
    (l) =>
      l.writing_line_uuid === writingLine.writing_line_uuid &&
      l.datum_type === writingLine.datum_type
  )
  if (lineToModifyIndex === -1 || Number(newAmount) < 0) {
    return modifiedLines
  }

  modifiedLines[lineToModifyIndex].amount = newAmount.replace(",", ".")

  if (vatTypeArray.includes(writingLine.datum_type as VatType)) {
    const oppositeVatType = writingLine.datum_type.includes("_reverse_vat")
      ? writingLine.datum_type.replace("_reverse_vat", "_vat")
      : writingLine.datum_type.replace("_vat", "_reverse_vat")

    const lineToModifyIndex = modifiedLines.findIndex(
      (l) =>
        l.writing_line_uuid === writingLine.writing_line_uuid &&
        l.datum_type === oppositeVatType
    )
    modifiedLines[lineToModifyIndex].amount = newAmount.replace(",", ".")
  }

  const updatedLines = updateTaxIncluded(modifiedLines)

  return updatedLines
}

export const updateTaxIncluded = (modifiedLines: WritingLinePayload[]) => {
  const updatedLines = [...modifiedLines]

  let newTotal = Dinero({ amount: 0 })
  let reverseDepositTotal = Dinero({ amount: 0 })

  const direction = updatedLines[updatedLines.length - 1].direction

  updatedLines.forEach((l, index) => {
    if (index !== updatedLines.length - 1) {
      const lineAmount = getDineroAmount(l.amount)
      if (l.direction === direction) {
        newTotal = newTotal.subtract(lineAmount)
        if (l.datum_type === "reverse_deposit") {
          reverseDepositTotal = reverseDepositTotal.add(lineAmount)
        }
      } else {
        newTotal = newTotal.add(lineAmount)
      }
    } else {
      // tax_included
      const newTotalFormatted = newTotal.toUnit().toString()
      l.amount = newTotalFormatted.includes(".")
        ? newTotalFormatted
        : `${newTotalFormatted}.00`
      if (newTotal.lessThan(Dinero({ amount: 0 }))) {
        l.direction = l.direction === "credit" ? "debit" : "credit"
        l.amount = l.amount.replace("-", "")
      }
    }
  })

  return updatedLines
}

export interface VatAccountsWithRate extends VatAccountPayload {
  vat_rate: string | null // be careful, "10.0" instead of "10"
}

interface AccountsConfiguration {
  merchant_buy_default_account_id: number | null
  merchant_buy_vat_default_account_id: number | null
  merchant_sell_default_account_id: number | null
  merchant_sell_vat_default_account_id: number | null
  vat_accounts: VatAccountsWithRate[]
  buy_or_sell: "buy" | "sell"
}

const getDefaultTaxExcludedDirection = (
  documentType: DocumentType,
  buyOrSell: buyOrSell
) => {
  switch (documentType) {
    case "invoice":
      return buyOrSell === "buy" ? "debit" : "credit"
    case "credit_note":
      return buyOrSell === "buy" ? "credit" : "debit"
    case "bill_of_exchange":
      return buyOrSell === "buy" ? "debit" : "credit"
  }
}

export const createWritingLine = ({
  existingLines,
  selectedOption,
  possibleAccounts,
  accountsConfiguration,
  documentType,
  buyThirdPartyAccount,
  autoliquidation_vat_rates,
  currency,
  allowedVatRates,
  all_company_accounts,
  vat_accounts,
  goods_or_services,
  autoliquidated_custom_vat_rates,
}: {
  existingLines: WritingLinePayload[]
  selectedOption: LineToAdd
  possibleAccounts: AllAccounts
  accountsConfiguration: AccountsConfiguration
  documentType: DocumentType
  buyThirdPartyAccount: Account | null
  autoliquidation_vat_rates: AutoliquidationVat
  currency: MainCurrencyCode
  allowedVatRates: number[]
  all_company_accounts: AllCompanyAccounts | null
  vat_accounts: VatAccountsWithRate[]
  goods_or_services: GoodsOrServices
  autoliquidated_custom_vat_rates: AutoliquidatedCustomVatRate[]
}) => {
  const taxIncludedLine = existingLines.pop() as WritingLinePayload

  const defaultTaxExcludedDirection = getDefaultTaxExcludedDirection(
    documentType,
    accountsConfiguration.buy_or_sell
  )
  const { linesToAdd, newLineUuid } = getLinesToAdd({
    existingLines,
    selectedOption,
    currencyCode: currency,
    direction: defaultTaxExcludedDirection,
    possibleAccounts,
    accountsConfiguration,
    buyThirdPartyAccount,
    autoliquidation_vat_rates: autoliquidation_vat_rates,
    allowedVatRates,
    all_company_accounts,
    vat_accounts,
    goods_or_services,
    autoliquidated_custom_vat_rates,
  })
  return {
    newLineUuid,
    modifiedLines: [...existingLines, ...linesToAdd, taxIncludedLine],
  }
}

const getLinesToAdd = ({
  existingLines,
  selectedOption,
  currencyCode,
  direction,
  possibleAccounts,
  accountsConfiguration,
  buyThirdPartyAccount,
  autoliquidation_vat_rates,
  allowedVatRates,
  all_company_accounts,
  vat_accounts,
  goods_or_services,
  autoliquidated_custom_vat_rates,
}: {
  existingLines: WritingLinePayload[]
  selectedOption: LineToAdd
  currencyCode: CurrencyCode
  direction: CreditOrDebit
  possibleAccounts: AllAccounts
  accountsConfiguration: AccountsConfiguration
  buyThirdPartyAccount: Account | null
  autoliquidation_vat_rates: AutoliquidationVat
  allowedVatRates: number[]
  all_company_accounts: AllCompanyAccounts | null
  vat_accounts: VatAccountsWithRate[]
  goods_or_services: GoodsOrServices
  autoliquidated_custom_vat_rates: AutoliquidatedCustomVatRate[]
}): { linesToAdd: WritingLinePayload[]; newLineUuid: string } => {
  const newLineUuid = uuidv4()

  const vatOptions: LineToAdd[] = allowedVatRates.map(toString)

  const all_accounts: AllAccounts | null =
    (all_company_accounts &&
      all_company_accounts[accountsConfiguration.buy_or_sell]) ||
    null

  const exchange_rate = existingLines[0]?.exchange_rate
  const exchange_rate_date = existingLines[0]?.exchange_rate_date
  const currency_amount = 0

  if (vatOptions.includes(selectedOption)) {
    const vat_account = getAccountForGoodsAndService({
      buy_or_sell: accountsConfiguration.buy_or_sell,
      goods_or_services,
      vat_accounts,
      vat_rate: selectedOption,
      all_accounts,
    })

    return {
      newLineUuid,
      linesToAdd: [
        {
          account: getTaxExcludedAccount({
            accountsConfiguration,
            possibleAccounts,
          }),
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type: "tax_excluded",
          direction,
          vat_rate: selectedOption,
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
        {
          account: vat_account
            ? {
                id: vat_account.id,
                number: vat_account.number,
                details: vat_account.details,
              }
            : null,
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type: "vat",
          direction,
          vat_rate: selectedOption,
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
      ],
    }
  }

  if (selectedOption === "0") {
    return {
      newLineUuid,
      linesToAdd: [
        {
          account: getTaxExcludedAccount({
            accountsConfiguration,
            possibleAccounts,
          }),
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type: "tax_excluded",
          direction,
          vat_rate: "0",
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
      ],
    }
  }

  if (selectedOption === "custom_credit") {
    return {
      newLineUuid,
      linesToAdd: [
        {
          account: getTaxExcludedAccount({
            accountsConfiguration,
            possibleAccounts,
          }),
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type: "custom",
          direction: "credit",
          vat_rate: "0",
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
      ],
    }
  }

  if (selectedOption === "custom_debit") {
    return {
      newLineUuid,
      linesToAdd: [
        {
          account: getTaxExcludedAccount({
            accountsConfiguration,
            possibleAccounts,
          }),
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type: "custom",
          direction: "debit",
          vat_rate: "0",
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
      ],
    }
  }

  if (selectedOption === "deposit") {
    return {
      newLineUuid,
      linesToAdd: [
        {
          account: possibleAccounts.deposit[0],
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type: "deposit",
          direction,
          vat_rate: "0",
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
      ],
    }
  }

  if (selectedOption === "reverse_deposit") {
    return {
      newLineUuid,
      linesToAdd: [
        {
          account: possibleAccounts.reverse_deposit[0],
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type: "reverse_deposit",
          direction: direction === "credit" ? "debit" : "credit",
          vat_rate: "0",
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
      ],
    }
  }

  if (autoliquidatedVatTypes.includes(selectedOption)) {
    const vat = selectedOption as keyof AutoliquidationVat
    const reverseVat = selectedOption.replace(
      "_vat",
      "_reverse_vat"
    ) as keyof AutoliquidationVat

    return {
      newLineUuid,
      linesToAdd: [
        {
          account: getTaxExcludedAccount({
            accountsConfiguration,
            possibleAccounts,
          }),
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type: "tax_excluded",
          direction,
          vat_rate: String(autoliquidation_vat_rates[vat]),
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
        {
          account: possibleAccounts[vat][0],
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type: vat as AllEntryDatumTypes,
          direction,
          vat_rate: String(autoliquidation_vat_rates[vat]),
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
        {
          account: possibleAccounts[reverseVat][0],
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type: reverseVat as AllEntryDatumTypes,
          direction: direction === "credit" ? "debit" : "credit",
          vat_rate: String(
            autoliquidation_vat_rates[vat as keyof AutoliquidationVat]
          ),
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
      ],
    }
  }

  if (
    selectedOption === "tax_excluded_bill_of_exchange" &&
    buyThirdPartyAccount
  ) {
    return {
      newLineUuid,
      linesToAdd: [
        {
          account: buyThirdPartyAccount,
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type: "tax_excluded_bill_of_exchange",
          direction,
          vat_rate: "",
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
      ],
    }
  }

  const customVatRatesTypes = [
    "eu_custom",
    "eu_goods_custom",
    "world_custom",
    "world_goods_custom",
    "construction_custom",
  ]
  if (customVatRatesTypes.some((c) => selectedOption.includes(c))) {
    const parts = selectedOption.split("_")

    const autoliquidated_type = selectedOption.split("_custom")[0]
    const rate = parts[parts.length - 1]
    const customVatRate = autoliquidated_custom_vat_rates.find(
      (c) =>
        c.autoliquidated_type === autoliquidated_type &&
        c.rate.replace(".0", "") === rate
    )
    if (!customVatRate) {
      return {
        newLineUuid: "",
        linesToAdd: [],
      }
    }

    const account = {
      id: customVatRate.vat_account_id,
      number: customVatRate.vat_account_number.toString(),
      details: customVatRate.vat_account_details,
    }
    const reverseAccount = {
      id: customVatRate.reverse_vat_account_id,
      number: customVatRate.reverse_vat_account_number.toString(),
      details: customVatRate.reverse_vat_account_details,
    }
    const vatRate = customVatRate?.rate

    return {
      newLineUuid,
      linesToAdd: [
        {
          account: getTaxExcludedAccount({
            accountsConfiguration,
            possibleAccounts,
          }),
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type: "tax_excluded",
          direction,
          vat_rate: vatRate,
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
        {
          account: account,
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type: `${autoliquidated_type}_vat` as AllEntryDatumTypes,
          direction,
          vat_rate: vatRate,
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
        {
          account: reverseAccount,
          writing_line_uuid: newLineUuid,
          amount: "0",
          currency_code: currencyCode,
          exchange_rate: exchange_rate,
          exchange_rate_date: exchange_rate_date,
          currency_amount: currency_amount,
          datum_type:
            `${autoliquidated_type}_reverse_vat` as AllEntryDatumTypes,
          direction: direction === "credit" ? "debit" : "credit",
          vat_rate: vatRate,
          source: "input_mask",
          merchant_code_id: null,
          is_fuel_vat_line: false,
          partial_vat_recovery_percentage: null,
          unit: { id: null },
          unit_quantity: null,
          unit_description: null,
          unit_2: { id: null },
          unit_2_quantity: null,
          unit_2_description: null,
          analytic_id: null,
          new_analytic_value: null,
        },
      ],
    }
  }

  return {
    newLineUuid: "",
    linesToAdd: [],
  }
}

const getTaxExcludedAccount = ({
  accountsConfiguration,
  possibleAccounts,
}: {
  accountsConfiguration: AccountsConfiguration
  possibleAccounts: AllAccounts
}): Account | null => {
  if (accountsConfiguration.buy_or_sell === "buy") {
    const accountId = accountsConfiguration.merchant_buy_default_account_id
    return possibleAccounts.tax_excluded.find((a) => a.id === accountId) || null
  }

  if (accountsConfiguration.buy_or_sell === "sell") {
    const accountId = accountsConfiguration.merchant_sell_default_account_id
    return possibleAccounts.tax_excluded.find((a) => a.id === accountId) || null
  }

  return null
}

export const isMagicCheckBoxDatumType = (
  datumType: AllEntryDatumTypes
): datumType is MagicCheckBoxDatumTypes => {
  return magicCheckBoxDatumTypes.includes(datumType as MagicCheckBoxDatumTypes)
}

const isTransferGoods = (
  datumType: AllEntryDatumTypes
): datumType is TransferGoodsDatumType => {
  return transferGoodsDatumTypes.includes(datumType as TransferGoodsDatumType)
}

export const getOptionsInfo = ({
  option,
  editedWritingLines,
  buyOrSell,
  possibleAccounts,
  companyOnboarding,
}: {
  option: string
  editedWritingLines: WritingLinePayload[]
  buyOrSell: "buy" | "sell" | undefined
  possibleAccounts: AllAccounts
  companyOnboarding: IsOnboardingFinishedType
}): { isOptionEnabled: boolean; tooltipKey?: string } => {
  if (companyOnboarding === "unfinished")
    return {
      isOptionEnabled: false,
      tooltipKey: "input-mask.select-add.tooltip.onboarding-unfinished",
    }

  if (
    editedWritingLines.some(
      (l) =>
        l != null &&
        transferGoodsDatumTypes.includes(l.datum_type as TransferGoodsDatumType)
    )
  ) {
    if (["custom_debit", "custom_credit"].includes(option)) {
      return {
        isOptionEnabled: true,
        tooltipKey: "",
      }
    }
    return {
      isOptionEnabled: false,
      tooltipKey: "input-mask.select-add.tooltip.goods-mixup",
    }
  }

  if (buyOrSell === undefined) return { isOptionEnabled: false }

  const customOptions: LineToAdd[] = ["custom_credit", "custom_debit"]

  if (customOptions.includes(option)) return { isOptionEnabled: true }

  // buralistes : block adding new lines
  if (
    editedWritingLines.some((l) => {
      return isTransferGoods(l.datum_type)
    })
  )
    return { isOptionEnabled: false }

  if (option === "deposit" && possibleAccounts.deposit.length === 0)
    return {
      isOptionEnabled: false,
      tooltipKey: "input-mask.select-add.tooltip.missing-account",
    }

  if (
    option === "reverse_deposit" &&
    possibleAccounts.reverse_deposit.length === 0
  )
    return {
      isOptionEnabled: false,
      tooltipKey: "input-mask.select-add.tooltip.missing-account",
    }

  const vatTypeOption = option
  const reverseVatTypeOption = option.replace("_vat", "_reverse_vat")

  if (autoliquidatedVatTypes.includes(option)) {
    if (buyOrSell === "sell")
      return {
        isOptionEnabled: false,
        tooltipKey: "input-mask.select-add.tooltip.autoliquidation-sell",
      }

    if (
      possibleAccounts[vatTypeOption as keyof AllAccounts]?.length === 0 ||
      possibleAccounts[reverseVatTypeOption as keyof AllAccounts]?.length === 0
    )
      return {
        isOptionEnabled: false,
        tooltipKey: "input-mask.select-add.tooltip.missing-vat-account",
      }

    if (
      !editedWritingLines
        .filter((l) => l.datum_type === "tax_excluded")
        .every((taxEcludedLine) =>
          editedWritingLines.find((l) => {
            return (
              l.writing_line_uuid === taxEcludedLine.writing_line_uuid &&
              String(l.datum_type) === String(option)
            )
          })
        )
    )
      return {
        isOptionEnabled: false,
        tooltipKey: "input-mask.select-add.tooltip.multiple-vat",
      }

    const conditions = [
      "deposit",
      "reverse_deposit",
      "construction_vat",
      "construction_reverse_vat",
      "vat",
      "eu_vat",
      "eu_reverse_vat",
      "eu_goods_vat",
      "eu_goods_reverse_vat",
      "world_vat",
      "world_reverse_vat",
      "world_goods_vat",
      "world_goods_reverse_vat",
    ].filter((item) => item !== vatTypeOption && item !== reverseVatTypeOption)

    const isOptionEnabled = !editedWritingLines.some((l) =>
      conditions.includes(l.datum_type)
    )
    return {
      isOptionEnabled,
      tooltipKey: !isOptionEnabled
        ? "input-mask.select-add.tooltip.multiple-vat"
        : undefined,
    }
  }

  const customVatRatesTypes = [
    "eu_custom",
    "eu_goods_custom",
    "world_custom",
    "world_goods_custom",
    "construction_custom",
  ]
  if (customVatRatesTypes.some((c) => option.includes(c))) {
    const baseType = option.split("_custom")[0]
    const sameFamilyVatTypes = [
      `${baseType}_vat`,
      `${baseType}_reverse_vat`,
    ] as const
    const allVatConflictTypes = [
      "deposit",
      "reverse_deposit",
      "construction_vat",
      "construction_reverse_vat",
      "vat",
      "eu_vat",
      "eu_reverse_vat",
      "eu_goods_vat",
      "eu_goods_reverse_vat",
      "world_vat",
      "world_reverse_vat",
      "world_goods_vat",
      "world_goods_reverse_vat",
    ]
    const conditions = allVatConflictTypes.filter(
      (t) =>
        !sameFamilyVatTypes.includes(t as (typeof sameFamilyVatTypes)[number])
    )
    const isOptionEnabled = !editedWritingLines.some((l) =>
      conditions.includes(l.datum_type)
    )
    return {
      isOptionEnabled,
      tooltipKey: !isOptionEnabled
        ? "input-mask.select-add.tooltip.multiple-vat"
        : undefined,
    }
  }

  const isOptionEnabled = !editedWritingLines.some((l) => {
    return [
      "construction_vat",
      "eu_vat",
      "eu_goods_vat",
      "world_vat",
      "world_goods_vat",
    ].includes(l.datum_type)
  })

  return {
    isOptionEnabled,
    tooltipKey: !isOptionEnabled
      ? "input-mask.select-add.tooltip.multiple-vat"
      : undefined,
  }
}

interface GetDisplayedCode {
  buyOrSell: buyOrSell
  merchantCode?: MerchantCode
  buyAuxiliaryPrefix: string
  sellAuxiliaryPrefix: string
}
export const getDisplayedCode = ({
  buyOrSell,
  merchantCode,
  buyAuxiliaryPrefix,
  sellAuxiliaryPrefix,
}: GetDisplayedCode) => {
  if (!merchantCode) return ""

  const codePrefix =
    buyOrSell === "buy" ? buyAuxiliaryPrefix : sellAuxiliaryPrefix

  return `${codePrefix}${merchantCode.code}`
}

interface GetDisplayedBillOfExchangeCode {
  writingLine: WritingLinePayload
  buyOrSell: buyOrSell
  merchantCode: MerchantCode | null
  buyAuxiliaryPrefix: string
  sellAuxiliaryPrefix: string
  buyThirdPartyAccounNumber: string | undefined
}

export const getDisplayedBillOfExchangeCode = ({
  writingLine,
  buyOrSell,
  merchantCode,
  buyAuxiliaryPrefix,
  sellAuxiliaryPrefix,
  buyThirdPartyAccounNumber,
}: GetDisplayedBillOfExchangeCode) => {
  if (!merchantCode) return ""

  const codePrefix =
    buyOrSell === "buy" ? buyAuxiliaryPrefix : sellAuxiliaryPrefix

  if (merchantCode.auxiliary) {
    return `${
      writingLine.account?.number || buyThirdPartyAccounNumber
    }, ${codePrefix}${merchantCode.code}`
  } else {
    const prefix = buyOrSell === "buy" ? "401" : "411"
    return `${prefix}${codePrefix}${merchantCode.code}`
  }
}

interface GetDisplayedNumber {
  writingLines: WritingLinePayload[]
  buyOrSell: buyOrSell
  merchantCode?: MerchantCode
  buyAuxiliaryPrefix: string
  sellAuxiliaryPrefix: string
  accountsForBillOfExchange: Account[]
  billOfExhangeAccountByMonth: AccountBillOfExchange[]
  documentDate: string
  accounting_software_prefix_buy?: string
  accounting_software_prefix_sell?: string
}

export const getDisplayedNumber = ({
  writingLines,
  buyOrSell,
  merchantCode,
  buyAuxiliaryPrefix,
  sellAuxiliaryPrefix,
  accountsForBillOfExchange,
  billOfExhangeAccountByMonth,
  documentDate,
  accounting_software_prefix_buy = "",
  accounting_software_prefix_sell = "",
}: GetDisplayedNumber) => {
  if (!merchantCode) return ""
  const taxIncludedWritingLineAccount = writingLines.find(
    (l) =>
      l.datum_type === "tax_included" ||
      l.datum_type === "tax_included_bill_of_exchange"
  )?.account

  if (!taxIncludedWritingLineAccount) return ""

  if (
    writingLines.find((l) => l.datum_type === "tax_included_bill_of_exchange")
  ) {
    const month = documentDate.split("/")[1]
      ? Number(documentDate.split("/")[1])
      : 0

    const accountId =
      billOfExhangeAccountByMonth.find(
        (b) => b.month_number === month || b.month_number === 0
      )?.account_id || 0
    const account = accountsForBillOfExchange.find((a) => a.id === accountId)

    if (account) {
      return `${account.number} ${account.details}`
    }
  }

  const codePrefix =
    buyOrSell === "buy"
      ? `${accounting_software_prefix_buy}${buyAuxiliaryPrefix}`
      : `${accounting_software_prefix_sell}${sellAuxiliaryPrefix}`

  if (merchantCode.auxiliary) {
    return `${taxIncludedWritingLineAccount.number} ${taxIncludedWritingLineAccount.details} (${codePrefix}${merchantCode.code})`
  } else {
    const prefix = buyOrSell === "buy" ? "401" : "411"
    return `${prefix}${codePrefix}${merchantCode.code}`
  }
}

interface ChangeBuyOrSell {
  taxIncludedAccount: Account | undefined
  direction: CreditOrDebit
  currencyCode: CurrencyCode
  exchange_rate: number | null
  exchange_rate_date: string | null
  currency_amount: number | null
}

export const changeBuyOrSell = ({
  taxIncludedAccount,
  direction,
  currencyCode,
  exchange_rate,
  exchange_rate_date,
  currency_amount,
}: ChangeBuyOrSell): WritingLinePayload[] => {
  if (!taxIncludedAccount) {
    return []
  }
  const newLineUuid = uuidv4()

  return [
    {
      amount: "0.00",
      datum_type: "tax_included",
      writing_line_uuid: newLineUuid,
      direction,
      account: taxIncludedAccount,
      source: "input_mask",
      currency_code: currencyCode,
      vat_rate: "0",
      exchange_rate: exchange_rate,
      exchange_rate_date: exchange_rate_date,
      currency_amount: currency_amount,
      merchant_code_id: null,
      is_fuel_vat_line: false,
      partial_vat_recovery_percentage: null,
      unit: { id: null },
      unit_quantity: null,
      unit_description: null,
      unit_2: { id: null },
      unit_2_quantity: null,
      unit_2_description: null,
      analytic_id: null,
      new_analytic_value: null,
    },
  ]
}

interface ChangeDocumentType {
  currentWritingLines: WritingLinePayload[]
  accounts: AllAccounts | null
  buyOrSell: buyOrSell
  documentType: "invoice" | "credit_note"
}

export const changeDocumentType = ({
  currentWritingLines,
  accounts,
  buyOrSell,
  documentType,
}: ChangeDocumentType): WritingLinePayload[] => {
  return currentWritingLines.map((l) => {
    if (
      l.datum_type === "tax_included_bill_of_exchange" ||
      l.datum_type === "tax_included"
    ) {
      const direction =
        documentType === "invoice"
          ? buyOrSell === "buy"
            ? "credit"
            : "debit"
          : buyOrSell === "buy" // credit_note
          ? "debit"
          : "credit"

      const account = accounts?.tax_included[0]
      return {
        ...l,
        datum_type: "tax_included",
        direction,
        account: {
          id: account?.id || 0,
          number: account?.number || "",
          details: account?.details || "",
        },
      }
    } else {
      return {
        ...l,
        direction: l.direction === "credit" ? "debit" : "credit",
      }
    }
  })
}

interface UpdatedAmout {
  newAmount: string
  newCurrencyAmount: string
}

export const getAmountFromUserInput = ({
  userInput,
  exchangeRate,
}: {
  userInput: string
  exchangeRate: number | null
}): UpdatedAmout | null => {
  if (userInput === "") {
    return {
      newAmount: "0",
      newCurrencyAmount: "0",
    }
  }
  let userInputWithoutSpaces = userInput.replace(/ /g, "")
  const matchUnAuthorizedCharacter =
    userInputWithoutSpaces.match(/^[\d+\-,.()*/]+$/)
  if (!matchUnAuthorizedCharacter) {
    return null
  }

  let userInputWithoutComma = userInputWithoutSpaces.replace(/,/g, ".")
  userInputWithoutComma =
    userInputWithoutComma[0] === "0" && userInputWithoutComma.length > 1
      ? userInputWithoutComma.substring(1)
      : userInputWithoutComma

  let evaluatedAmount

  try {
    // eslint-disable-next-line no-eval
    evaluatedAmount = eval(userInputWithoutComma)
  } catch {
    try {
      if (
        ["/", "+", "-", "*", "(", ")"].some((operand) =>
          userInput.endsWith(operand)
        )
      ) {
        const removedLast = userInput
          .substring(0, userInput.length - 1)
          .replace(/,/g, ".")

        try {
          // eslint-disable-next-line no-eval
          evaluatedAmount = (0, eval)(removedLast)
        } catch {
          try {
            const removed2Last = ["/", "+", "-", "*"].includes(
              removedLast[removedLast.length - 1]
            )
              ? removedLast
                  .substring(0, removedLast.length - 1)
                  .replace(/,/g, ".")
              : removedLast

            // eslint-disable-next-line no-eval
            evaluatedAmount = (0, eval)(removed2Last.replace(/[()]/g, ""))
          } catch {
            return null
          }
        }
      } else {
        try {
          // eslint-disable-next-line no-eval
          evaluatedAmount = (0, eval)(
            userInputWithoutComma.replace(/[()]/g, "")
          )
        } catch {
          return null
        }
      }
    } catch {
      return null
    }
  }

  if (!isFinite(evaluatedAmount)) {
    return null
  }
  const newAmountNumber = parseFloat(String(evaluatedAmount.toFixed(2)))
  const newCurrencyAmount = exchangeRate
    ? (exchangeRate * newAmountNumber).toFixed(2)
    : ""

  return {
    newAmount: String(newAmountNumber),
    newCurrencyAmount,
  }
}

export const getGroupedLines = (sortedWritingLines: WritingLinePayload[]) => {
  const grouped = sortedWritingLines.reduce<WritingLinePayload[][]>(
    (groups: WritingLinePayload[][], line: WritingLinePayload) => {
      const lastGroup = groups[groups.length - 1]
      if (
        lastGroup.some(
          (l: WritingLinePayload) =>
            l.writing_line_uuid === line.writing_line_uuid
        )
      ) {
        let groupToChange = groups.pop() || []
        const returnValue = [...groups, [...groupToChange, line]]
        return returnValue
      }
      const returnValue = [...groups, [line]]
      return returnValue
    },
    [[]]
  )

  grouped.shift()
  return grouped
}

export type AlreadySeenFilterOptions =
  | "input-mask.filter-dropdown.all"
  | "input-mask.filter-dropdown.not-seen"
  | "input-mask.filter-dropdown.already-seen"

export const filterAlreadySeenDocuments = ({
  sortedDocuments,
  alreadySeenFilterOption,
}: {
  sortedDocuments: FullDocumentForInputMaskWithNewMerchant[]
  alreadySeenFilterOption: AlreadySeenFilterOptions
}) => {
  const alreadySeenFullDocuments: number[] = JSON.parse(
    localStorage.getItem("seenFullDocuments") || "[]"
  )

  switch (alreadySeenFilterOption) {
    case "input-mask.filter-dropdown.all":
      return sortedDocuments
    case "input-mask.filter-dropdown.already-seen":
      return sortedDocuments.filter((d) =>
        alreadySeenFullDocuments.includes(d.fd_id)
      )
    case "input-mask.filter-dropdown.not-seen":
      return sortedDocuments.filter(
        (d) => !alreadySeenFullDocuments.includes(d.fd_id)
      )
  }
}

interface UnitToCreate {
  description: string | null
  quantity: number | null
  unit: number | null
}

interface DataToCheck {
  writingLine: WritingLinePayload
  unit1: UnitToCreate
  unit2: UnitToCreate
}

export const disableUnitButton = ({
  writingLine,
  unit1,
  unit2,
}: DataToCheck) => {
  if (isUnitIncomplete(unit1)) {
    return true
  }
  if (isUnitIncomplete(unit2)) {
    return true
  }
  if (
    unitWasModified(unit1, {
      description: writingLine.unit_description,
      quantity: writingLine.unit_quantity,
      unit: writingLine.unit.id,
    })
  ) {
    return false
  }
  if (
    unitWasModified(unit2, {
      description: writingLine.unit_2_description,
      quantity: writingLine.unit_2_quantity,
      unit: writingLine.unit_2.id,
    })
  ) {
    return false
  }
  return true
}

const isUnitIncomplete = (unit: UnitToCreate) => {
  if (unit.description === "" && unit.unit === null && unit.quantity === null)
    return false
  if (unit.description !== "" && unit.unit !== null && unit.quantity !== null)
    return false
  return true
}

const unitWasModified = (
  unit: UnitToCreate,
  unitFromWritingLine: UnitToCreate
) => {
  return (
    unit.description !== (unitFromWritingLine.description || "") ||
    unit.quantity !== (unitFromWritingLine.quantity || null) ||
    unit.unit !== unitFromWritingLine.unit
  )
}

const getAccountForGoodsAndService = ({
  buy_or_sell,
  goods_or_services,
  vat_accounts,
  vat_rate,
  all_accounts,
}: {
  buy_or_sell: buyOrSell
  goods_or_services: GoodsOrServices
  vat_accounts: VatAccountsWithRate[]
  vat_rate: string
  all_accounts: AllAccounts | null
}) => {
  if (vat_accounts.every((va) => !va.vat_rate)) {
    const vat_account = vat_accounts.find(
      (va) =>
        va.buy_or_sell === buy_or_sell &&
        va.goods_or_services === goods_or_services
    )
    return vat_account
      ? all_accounts?.vat.find((a) => a.id === vat_account.account_id)
      : all_accounts?.vat[0]
  }

  const vat_account_by_vat_rate = vat_accounts.find(
    (va) =>
      va.buy_or_sell === buy_or_sell &&
      va.goods_or_services === goods_or_services &&
      Number(va.vat_rate) === Number(vat_rate)
  )

  return vat_account_by_vat_rate
    ? all_accounts?.vat.find((a) => a.id === vat_account_by_vat_rate.account_id)
    : all_accounts?.vat[0]
}

export const updateGoodsOrServiceAndSourceLines = ({
  all_company_accounts,
  vat_accounts,
  buy_or_sell,
  goods_or_services,
  source,
  edited_writing_lines,
}: {
  all_company_accounts: AllCompanyAccounts | null
  vat_accounts: VatAccountsWithRate[]
  buy_or_sell: buyOrSell
  goods_or_services: GoodsOrServices
  source: DocumentSource
  edited_writing_lines: WritingLinePayload[]
}): WritingLinePayload[] => {
  const datumTypesToReplace = [
    "vat",
    "eu_vat",
    "eu_reverse_vat",
    "eu_goods_vat",
    "eu_goods_reverse_vat",
    "world_vat",
    "world_reverse_vat",
    "world_goods_vat",
    "world_goods_reverse_vat",
  ]

  const all_accounts: AllAccounts | null =
    (all_company_accounts && all_company_accounts[buy_or_sell]) || null

  return edited_writing_lines.map((l) => {
    if (
      goods_or_services &&
      source &&
      datumTypesToReplace.includes(l.datum_type)
    ) {
      const accountToSwitchTo = {
        on_site_consumption: {
          eu: {
            normal: null,
            reverse: null,
          },
          world: {
            normal: null,
            reverse: null,
          },
          fr: null,
        },
        goods: {
          eu: {
            normal: all_accounts?.eu_goods_vat && all_accounts?.eu_goods_vat[0],
            reverse:
              all_accounts?.eu_goods_reverse_vat &&
              all_accounts?.eu_goods_reverse_vat[0],
          },
          world: {
            normal:
              all_accounts?.world_goods_vat && all_accounts?.world_goods_vat[0],
            reverse:
              all_accounts?.world_goods_reverse_vat &&
              all_accounts?.world_goods_reverse_vat[0],
          },
          fr: getAccountForGoodsAndService({
            buy_or_sell,
            goods_or_services: "goods",
            vat_accounts,
            vat_rate: l.vat_rate,
            all_accounts,
          }),
        },
        services: {
          eu: {
            normal: all_accounts?.eu_vat && all_accounts?.eu_vat[0],
            reverse:
              all_accounts?.eu_reverse_vat && all_accounts?.eu_reverse_vat[0],
          },
          world: {
            normal: all_accounts?.world_vat && all_accounts?.world_vat[0],
            reverse:
              all_accounts?.world_reverse_vat &&
              all_accounts?.world_reverse_vat[0],
          },
          fr: getAccountForGoodsAndService({
            buy_or_sell,
            goods_or_services: "services",
            vat_accounts,
            vat_rate: l.vat_rate,
            all_accounts,
          }),
        },
      }

      const is_reverse = l.datum_type.includes("reverse")

      const accountObject =
        source === "fr"
          ? accountToSwitchTo[goods_or_services][source]
          : accountToSwitchTo[goods_or_services][source][
              is_reverse ? "reverse" : "normal"
            ]
      return {
        ...l,
        account: accountObject
          ? {
              id: accountObject.id,
              number: accountObject.number,
              details: accountObject.details,
            }
          : null,
        datum_type: getDatumType({
          previous_datum_type: l.datum_type,
          goods_or_services: goods_or_services,
        }),
      }
    }
    return l
  })
}

const getDatumType = ({
  previous_datum_type,
  goods_or_services,
}: {
  previous_datum_type: AllEntryDatumTypes
  goods_or_services: GoodsOrServices
}): AllEntryDatumTypes => {
  if (
    previous_datum_type.includes("_goods") &&
    goods_or_services === "services"
  ) {
    return previous_datum_type.replace("_goods", "") as AllEntryDatumTypes
  }

  if (
    !previous_datum_type.includes("_goods") &&
    previous_datum_type.includes("_reverse_vat") &&
    goods_or_services === "goods"
  ) {
    return previous_datum_type.replace(
      "_reverse_vat",
      "_goods_reverse_vat"
    ) as AllEntryDatumTypes
  }

  if (
    !previous_datum_type.includes("_goods") &&
    previous_datum_type.includes("_vat") &&
    goods_or_services === "goods"
  ) {
    return previous_datum_type.replace(
      "_vat",
      "_goods_vat"
    ) as AllEntryDatumTypes
  }

  return previous_datum_type
}
